import React, { useState, useEffect } from "react";
import { Tooltip } from "react-tooltip";
import {
  InformationCircleIcon,
  ArrowLeftCircleIcon,
  ArrowRightCircleIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  QuestionMarkCircleIcon,
  ChevronDownIcon,
  LockClosedIcon
} from "@heroicons/react/24/outline";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip,
} from "recharts";

// Updated official court fees for 2024
const OFFICIAL_FEES = {
  DEMANDE: 355, // Application fee
  REPONSE: 177, // Response fee
  DEMANDE_CONJOINTE: 128, // Joint application fee
  ADDITIONAL: {
    URGENT_MOTION: 150,
    CERTIFICATION: 20,
    COPIES: 15,
    STAMP_DUTY: 35
  }
};

// Updated cost ranges based on Quebec legal data
const COST_RANGES = {
  LAWYER: {
    HOURLY_RATES: {
      JUNIOR: { min: 150, max: 250 },
      INTERMEDIATE: { min: 250, max: 350 },
      SENIOR: { min: 350, max: 600 }
    },
    RETAINER: {
      AMIABLE: { min: 2000, max: 3500 },
      CONTESTE: { min: 5000, max: 10000 }
    },
    REGIONAL_MULTIPLIER: {
      MONTREAL: 1.2,
      QUEBEC: 1.1,
      OTHER: 1.0
    }
  },
  MEDIATION: {
    HOURLY_RATE: 130, // Updated 2024 rate
    FREE_HOURS: {
      WITH_CHILDREN: 5,
      WITHOUT_CHILDREN: 3
    },
    ADMIN_FEE: 50
  },
  HUISSIER: { 
    BASE: { min: 40, max: 200 },
    KM_RATE: 1.0,
    ATTEMPTS: { min: 1, max: 3 }
  },
  EXPERTISE: {
    PSYCHOSOCIAL: {
      BASIC: { min: 2000, max: 5000, label: "Expertise psychosociale (garde d'enfants)", description: "Évaluation des capacités parentales et recommandations pour la garde" },
      COMPLEX: { min: 5000, max: 10000, label: "Expertise psychosociale complexe", description: "Cas impliquant plusieurs enfants ou situations particulières" }
    },
    FINANCIAL: {
      SIMPLE: { min: 1500, max: 3000, label: "Analyse financière simple", description: "Évaluation des revenus et dépenses" },
      COMPLEX: { min: 3000, max: 8000, label: "Analyse financière complexe", description: "Analyse détaillée des actifs et passifs" },
      BUSINESS: { min: 5000, max: 15000, label: "Évaluation d'entreprise", description: "Analyse complète de la valeur d'une entreprise" }
    },
    REAL_ESTATE: {
      RESIDENTIAL: { min: 300, max: 600, label: "Évaluation résidentielle", description: "Évaluation d'une propriété résidentielle" },
      COMMERCIAL: { min: 1000, max: 5000, label: "Évaluation commerciale", description: "Évaluation de biens immobiliers commerciaux" }
    },
    PENSION: { 
      SIMPLE: { min: 150, max: 325, label: "Évaluation de régime de retraite simple", description: "Régime à cotisations déterminées" },
      COMPLEX: { min: 500, max: 2000, label: "Évaluation de régime de retraite complexe", description: "Régimes multiples ou prestations déterminées" }
    }
  }
};

// Complexity factors that affect total cost
const COMPLEXITY_FACTORS = {
  PROPERTIES: [
    { count: 1, multiplier: 1.0, description: "Une seule propriété" },
    { count: 2, multiplier: 1.3, description: "Deux propriétés" },
    { count: 3, multiplier: 1.6, description: "Trois propriétés" },
    { count: "4+", multiplier: 2.0, description: "Quatre propriétés ou plus" }
  ],
  CHILDREN: [
    { count: 0, multiplier: 1.0, description: "Pas d'enfants" },
    { count: 1, multiplier: 1.2, description: "Un enfant" },
    { count: 2, multiplier: 1.4, description: "Deux enfants" },
    { count: "3+", multiplier: 1.7, description: "Trois enfants ou plus" }
  ],
  BUSINESSES: [
    { count: 0, multiplier: 1.0, description: "Pas d'entreprise" },
    { count: 1, multiplier: 1.5, description: "Une entreprise" },
    { count: "2+", multiplier: 2.0, description: "Deux entreprises ou plus" }
  ],
  DURATION: [
    { months: 6, multiplier: 1.0, description: "Moins de 6 mois" },
    { months: 12, multiplier: 1.5, description: "6 à 12 mois" },
    { months: 24, multiplier: 2.0, description: "12 à 24 mois" },
    { months: "24+", multiplier: 2.5, description: "Plus de 24 mois" }
  ],
  INTERNATIONAL: 1.5
};

// Time estimates for different types of divorce
const TIME_ESTIMATES = {
  JOINT: {
    min: 3,
    max: 6,
    description: "Divorce conjoint (à l'amiable)"
  },
  UNCONTESTED: {
    min: 4,
    max: 8,
    description: "Divorce non contesté"
  },
  CONTESTED: {
    min: 12,
    max: 24,
    description: "Divorce contesté"
  },
  COMPLEX: {
    min: 24,
    max: 36,
    description: "Divorce complexe avec litiges"
  }
};

// Regional variations in Quebec
const REGIONS = {
  MONTREAL: {
    name: "Montréal",
    lawyer_multiplier: 1.2,
    court_wait_time: 1.2,
    description: "Région métropolitaine de Montréal"
  },
  QUEBEC: {
    name: "Québec",
    lawyer_multiplier: 1.1,
    court_wait_time: 1.1,
    description: "Région de la Capitale-Nationale"
  },
  LAVAL: {
    name: "Laval",
    lawyer_multiplier: 1.15,
    court_wait_time: 1.15,
    description: "Région de Laval"
  },
  OTHER: {
    name: "Autres régions",
    lawyer_multiplier: 1.0,
    court_wait_time: 1.0,
    description: "Autres régions du Québec"
  }
};

// Cost saving tips with detailed explanations
const SAVING_TIPS = [
  {
    title: "Optez pour la médiation familiale",
    description: "Profitez des séances gratuites de médiation (5h avec enfants, 3h sans enfants)",
    potential_savings: "3000$ - 5000$",
    difficulty: "Facile",
    prerequisites: "Volonté de coopération des deux parties"
  },
  {
    title: "Divorce conjoint",
    description: "Présentez une demande conjointe en divorce pour réduire les frais de cour",
    potential_savings: "227$ sur les frais de cour + économies importantes en frais d'avocat",
    difficulty: "Modérée",
    prerequisites: "Accord sur tous les aspects du divorce"
  },
  {
    title: "Préparez vos documents",
    description: "Rassemblez tous les documents financiers et légaux à l'avance",
    potential_savings: "500$ - 2000$ en frais d'avocat",
    difficulty: "Facile",
    prerequisites: "Organisation et accès aux documents"
  },
  {
    title: "Vérifiez l'admissibilité à l'aide juridique",
    description: "Si vos revenus sont sous le seuil, vous pourriez avoir droit à l'aide juridique",
    potential_savings: "5000$ - 15000$",
    difficulty: "Facile",
    prerequisites: "Revenus sous le seuil d'admissibilité"
  }
];

// Palettes de couleurs améliorées
const COLORS = {
  primary: {
    light: "#E3F2FD",
    main: "#2196F3",
    dark: "#1565C0",
  },
  secondary: {
    light: "#F3E5F5",
    main: "#9C27B0",
    dark: "#6A1B9A",
  },
  success: {
    light: "#E8F5E9",
    main: "#4CAF50",
    dark: "#2E7D32",
  },
  error: {
    light: "#FFEBEE",
    main: "#F44336",
    dark: "#C62828",
  },
  warning: {
    light: "#FFF8E1",
    main: "#FFC107",
    dark: "#F57F17",
  },
  info: {
    light: "#E1F5FE",
    main: "#03A9F4",
    dark: "#0277BD",
  },
  grey: {
    50: "#FAFAFA",
    100: "#F5F5F5",
    200: "#EEEEEE",
    300: "#E0E0E0",
    400: "#BDBDBD",
    500: "#9E9E9E",
    600: "#757575",
    700: "#616161",
    800: "#424242",
    900: "#212121",
  },
};

// Couleurs pour le graphique
const CHART_COLORS = [
  "#2196F3",
  "#9C27B0",
  "#4CAF50",
  "#F44336",
  "#FFC107",
  "#03A9F4",
];

// Étapes de l'estimateur (REPLACED WITH SECTIONS)
const steps = [
  {
    id: 1,
    title: "Type de divorce et région",
    icon: "⚖️",
    description: "Précisez le type de divorce et votre région pour une estimation plus précise.",
    questions: [
      {
        name: "region",
        type: "radio",
        label: "Dans quelle région du Québec résidez-vous ?",
        options: [
          { value: "MONTREAL", label: "Montréal" },
          { value: "QUEBEC", label: "Québec" },
          { value: "LAVAL", label: "Laval" },
          { value: "OTHER", label: "Autres régions" }
        ],
        tooltip: "Les coûts varient selon la région, notamment pour les honoraires d'avocat."
      },
      {
        name: "isContested",
        type: "radio",
        label: "Votre divorce est-il contesté ?",
        options: [
          { value: "no", label: "Non (à l'amiable)" },
          { value: "yes", label: "Oui (contesté)" }
        ],
        tooltip: "Un divorce contesté implique des procédures plus longues et plus coûteuses."
      },
      {
        name: "filingType",
        type: "radio",
        label: "Type de demande",
        options: [
          {
            value: "conjointe",
            label: "Demande conjointe (128 $)",
            showIf: (ans) => ans.isContested === "no"
          },
          { value: "demande", label: "Je dépose la demande (355 $)" },
          { value: "reponse", label: "Je dépose la réponse (177 $)" },
          { value: "none", label: "Je ne paie pas les frais de cour (0 $)" }
        ],
        tooltip: "Les frais de cour varient selon le type de demande."
      }
    ]
  },
  {
    id: 2,
    title: "Situation familiale",
    icon: "👨‍👩‍👧‍👦",
    description: "Ces informations nous aident à évaluer la complexité de votre dossier.",
    questions: [
      {
        name: "childCount",
        type: "radio",
        label: "Combien d'enfants sont concernés ?",
        options: [
          { value: "0", label: "Aucun enfant" },
          { value: "1", label: "1 enfant" },
          { value: "2", label: "2 enfants" },
          { value: "3+", label: "3 enfants ou plus" }
        ],
        tooltip: "Le nombre d'enfants affecte les coûts de médiation et d'expertise psychosociale."
      },
      {
        name: "propertyCount",
        type: "radio",
        label: "Combien de propriétés sont à partager ?",
        options: [
          { value: "0", label: "Aucune propriété" },
          { value: "1", label: "1 propriété" },
          { value: "2", label: "2 propriétés" },
          { value: "3", label: "3 propriétés" },
          { value: "4+", label: "4 propriétés ou plus" }
        ],
        tooltip: "Chaque propriété peut nécessiter une évaluation."
      },
      {
        name: "businessCount",
        type: "radio",
        label: "Y a-t-il des entreprises à évaluer ?",
        options: [
          { value: "0", label: "Aucune entreprise" },
          { value: "1", label: "1 entreprise" },
          { value: "2+", label: "2 entreprises ou plus" }
        ],
        tooltip: "L'évaluation d'entreprises augmente significativement les coûts."
      },
      {
        name: "hasInternational",
        type: "radio",
        label: "Y a-t-il des aspects internationaux ?",
        options: [
          { value: "no", label: "Non" },
          { value: "yes", label: "Oui (biens à l'étranger, documents à traduire, etc.)" }
        ],
        tooltip: "Les aspects internationaux augmentent la complexité et les coûts."
      }
    ]
  },
  {
    id: 3,
    title: "Services juridiques",
    icon: "👨‍⚖️",
    description: "Déterminez vos besoins en services juridiques.",
    questions: [
      {
        name: "wantLawyer",
        type: "radio",
        label: "Souhaitez-vous engager un avocat ?",
        options: [
          { value: "true", label: "Oui" },
          { value: "false", label: "Non (0 $)" }
        ],
        tooltip: "Un avocat peut être essentiel, surtout dans les cas complexes."
      },
      {
        name: "lawyerExperience",
        type: "radio",
        label: "Quel niveau d'expérience d'avocat préférez-vous ?",
        options: [
          { value: "JUNIOR", label: "Junior (150-250$/h)" },
          { value: "INTERMEDIATE", label: "Intermédiaire (250-350$/h)" },
          { value: "SENIOR", label: "Senior (350-600$/h)" }
        ],
        showIf: (ans) => ans.wantLawyer === "true",
        tooltip: "Les taux horaires varient selon l'expérience."
      },
      {
        name: "lawyerShared",
        type: "radio",
        label: "Partagez-vous l'avocat avec l'autre partie ?",
        options: [
          { value: "yes", label: "Oui (frais partagés)" },
          { value: "no", label: "Non (avocats séparés)" }
        ],
        showIf: (ans) => ans.wantLawyer === "true" && ans.isContested === "no",
        tooltip: "Partager un avocat peut réduire les coûts dans un divorce à l'amiable."
      }
    ]
  },
  {
    id: 4,
    title: "Médiation et expertises",
    icon: "🤝",
    description: "Évaluez vos besoins en médiation et expertises.",
    questions: [
      {
        name: "wantMediation",
        type: "radio",
        label: "Souhaitez-vous utiliser la médiation familiale ?",
        options: [
          { value: "yes", label: "Oui" },
          { value: "no", label: "Non" }
        ],
        tooltip: "La médiation est gratuite pour les premières heures (5h avec enfants, 3h sans)."
      },
      {
        name: "expertiseTypes",
        type: "checkbox",
        label: "Quels types d'expertises seront nécessaires ?",
        options: [
          {
            value: "psychosocial",
            label: "Expertise psychosociale",
            showIf: (ans) => ans.childCount !== "0",
            description: "Évaluation pour la garde d'enfants",
            cost: "2000$ - 5000$"
          },
          {
            value: "financial",
            label: "Analyse financière",
            description: "Évaluation des actifs et revenus",
            cost: "1500$ - 8000$"
          },
          {
            value: "business",
            label: "Évaluation d'entreprise",
            showIf: (ans) => ans.businessCount !== "0",
            description: "Analyse complète de la valeur d'entreprise",
            cost: "5000$ - 15000$"
          },
          {
            value: "pension",
            label: "Évaluation de régime de retraite",
            description: "Calcul de la valeur des régimes de retraite",
            cost: "150$ - 2000$"
          }
        ],
        tooltip: "Les expertises sont souvent nécessaires pour des évaluations précises."
      }
    ]
  },
  {
    id: 5,
    title: "Services d'huissier",
    icon: "📋",
    description: "Évaluez vos besoins en services d'huissier.",
    questions: [
      {
        name: "needHuissier",
        type: "radio",
        label: "Aurez-vous besoin d'un huissier ?",
        options: [
          { value: "no", label: "Non (0 $)" },
          { value: "yes", label: "Oui" }
        ],
        tooltip: "Un huissier est nécessaire pour signifier certains documents."
      },
      {
        name: "huissierDistance",
        type: "radio",
        label: "À quelle distance se trouve l'autre partie ?",
        options: [
          { value: "local", label: "Même ville (40$ - 80$)" },
          { value: "regional", label: "Région différente (80$ - 150$)" },
          { value: "far", label: "Très éloignée (150$ - 200$)" }
        ],
        showIf: (ans) => ans.needHuissier === "yes",
        tooltip: "Les frais d'huissier augmentent avec la distance."
      },
      {
        name: "huissierServices",
        type: "slider",
        label: "Nombre de services requis",
        min: 1,
        max: 10,
        showIf: (ans) => ans.needHuissier === "yes",
        tooltip: "Chaque signification compte comme un service distinct."
      }
    ]
  },
  {
    id: 6,
    title: "Résumé et recommandations",
    icon: "💰",
    description: "Consultez votre estimation détaillée et nos recommandations.",
    questions: []
  }
];

// NEW: Group questions into logical sections
const sections = [
  {
    id: "basicInfo",
    title: "Informations de base",
    icon: "📄",
    description: "Commençons par quelques informations générales sur votre situation.",
    questions: [
      {
        name: "region",
        type: "radio",
        label: "Dans quelle région du Québec résidez-vous ?",
        options: [
          { value: "MONTREAL", label: "Montréal" },
          { value: "QUEBEC", label: "Québec" },
          { value: "LAVAL", label: "Laval" },
          { value: "OTHER", label: "Autres régions" }
        ],
        helpText: "Les coûts (honoraires d'avocat, délais de cour) peuvent varier selon votre région."
      },
      {
        name: "isContested",
        type: "radio",
        label: "Votre divorce est-il contesté ou à l'amiable ?",
        options: [
          { value: "no", label: "À l'amiable (accord sur la plupart des points)" },
          { value: "yes", label: "Contesté (désaccord sur des points majeurs)" }
        ],
        helpText: "Un divorce contesté est généralement plus long et plus coûteux car il nécessite plus d'interventions juridiques et judiciaires."
      },
      {
        name: "filingType",
        type: "radio",
        label: "Comment la demande de divorce sera-t-elle déposée ?",
        options: [
          {
            value: "conjointe",
            label: "Demande conjointe (accord mutuel)",
            showIf: (ans) => ans.isContested === "no",
            helpText: "Moins cher (128 $). Possible uniquement si vous êtes d'accord sur tout."
          },
          {
            value: "demande",
            label: "Je dépose la demande (introductive d'instance)",
            helpText: "Coût standard (355 $). Utilisé si vous initiez la procédure."
          },
          {
            value: "reponse",
            label: "Je réponds à une demande (défense)",
            helpText: "Coût standard (177 $). Utilisé si votre ex-conjoint a déposé la demande."
          },
          { value: "none",
            label: "Je ne paie pas les frais de cour (ex: aide juridique)",
            helpText: "Si vous bénéficiez de l'aide juridique, vous n'aurez peut-être pas à payer ces frais."
          }
        ],
        helpText: "Les frais de cour initiaux dépendent de qui dépose quoi. D'autres frais peuvent s'ajouter."
      }
    ]
  },
  {
    id: "familySituation",
    title: "Situation familiale et financière",
    icon: "👨‍👩‍👧‍👦",
    description: "Ces détails aident à estimer la complexité et les expertises potentiellement nécessaires.",
    questions: [
      {
        name: "childCount",
        type: "radio",
        label: "Combien d'enfants mineurs sont concernés par la séparation ?",
        options: [
          { value: "0", label: "Aucun enfant" },
          { value: "1", label: "1 enfant" },
          { value: "2", label: "2 enfants" },
          { value: "3+", label: "3 enfants ou plus" }
        ],
        helpText: "Le nombre d'enfants influence la médiation (plus d'heures gratuites), et peut nécessiter une expertise psychosociale si la garde est contestée."
      },
      {
        name: "propertyCount",
        type: "radio",
        label: "Combien de propriétés immobilières possédez-vous (ensemble ou séparément) ?",
        options: [
          { value: "0", label: "Aucune propriété" },
          { value: "1", label: "1 propriété" },
          { value: "2", label: "2 propriétés" },
          { value: "3", label: "3 propriétés" },
          { value: "4+", label: "4 propriétés ou plus" }
        ],
        helpText: "Le partage des propriétés peut nécessiter des évaluations immobilières et complexifier le règlement."
      },
      {
        name: "businessCount",
        type: "radio",
        label: "Y a-t-il des entreprises appartenant à l'un ou aux deux conjoints ?",
        options: [
          { value: "0", label: "Aucune entreprise" },
          { value: "1", label: "1 entreprise" },
          { value: "2+", label: "2 entreprises ou plus" }
        ],
        helpText: "L'évaluation de la valeur d'une entreprise est une expertise coûteuse et complexe."
      },
       {
        name: "hasPension", // New question about pensions
        type: "radio",
        label: "Y a-t-il des régimes de retraite (REER exclus) à partager ?",
        options: [
          { value: "yes", label: "Oui" },
          { value: "no", label: "Non / Ne sait pas" }
        ],
        helpText: "Le partage des régimes de retraite (fonds de pension agréés, etc.) peut nécessiter une évaluation actuarielle."
      },
      {
        name: "hasInternational",
        type: "radio",
        label: "Y a-t-il des aspects internationaux (ex: biens à l'étranger, résidence hors Canada) ?",
        options: [
          { value: "no", label: "Non" },
          { value: "yes", label: "Oui" }
        ],
        helpText: "Les éléments internationaux peuvent compliquer la procédure et nécessiter des expertises supplémentaires (droit étranger, traduction, etc.)."
      }
    ]
  },
  {
    id: "legalServices",
    title: "Services juridiques et médiation",
    icon: "👨‍⚖️🤝",
    description: "Comment comptez-vous gérer les aspects légaux et la négociation ?",
    questions: [
      {
        name: "wantLawyer",
        type: "radio",
        label: "Comptez-vous engager un avocat ?",
        options: [
          { value: "true", label: "Oui" },
          { value: "false", label: "Non" }
        ],
        helpText: "Un avocat est fortement recommandé, surtout si le divorce est contesté ou complexe. Vous pouvez aussi consulter pour des avis ponctuels."
      },
      {
        name: "lawyerExperience",
        type: "radio",
        label: "Si oui, quel niveau d'expérience recherchez-vous principalement ?",
        options: [
          { value: "JUNIOR", label: "Junior (150-250$/h)" },
          { value: "INTERMEDIATE", label: "Intermédiaire (250-350$/h)" },
          { value: "SENIOR", label: "Senior (350-600$/h)" }
        ],
        showIf: (ans) => ans.wantLawyer === "true",
        helpText: "Les taux horaires varient. Un avocat plus expérimenté peut coûter plus cher à l'heure mais être plus efficace."
      },
      {
        name: "lawyerShared",
        type: "radio",
        label: "Partagerez-vous les services d'un même avocat (possible si à l'amiable) ?",
        options: [
          { value: "yes", label: "Oui, nous utiliserons le même avocat" },
          { value: "no", label: "Non, chacun aura son propre avocat (ou un seul en aura)" }
        ],
        showIf: (ans) => ans.wantLawyer === "true" && ans.isContested === "no",
        helpText: "Partager un avocat réduit les coûts, mais n'est possible qu'en cas d'entente complète et nécessite une renonciation au conflit d'intérêts."
      },
      {
        name: "wantMediation",
        type: "radio",
        label: "Envisagez-vous la médiation familiale ?",
        options: [
          { value: "yes", label: "Oui" },
          { value: "maybe", label: "Peut-être / Ne sait pas" },
          { value: "no", label: "Non" }
        ],
        helpText: "Fortement recommandée pour régler les désaccords. Les premières heures sont gratuites (5h avec enfants, 3h sans). Peut éviter des frais d'avocat et de cour."
      }
    ]
  },
   {
    id: "expertise",
    title: "Expertises potentielles",
    icon: "🔍",
    description: "Cochez les expertises qui pourraient être nécessaires dans votre cas. Elles sont souvent coûteuses.",
    questions: [
      {
        name: "expertiseTypes",
        type: "checkbox",
        label: "Quels types d'expertises pourraient être nécessaires ?",
        options: [
          {
            value: "psychosocial",
            label: "Expertise psychosociale (garde d'enfants)",
            showIf: (ans) => ans.childCount !== "0" && ans.isContested === "yes", // Show only if children AND contested
            description: "Nécessaire si la garde ou les accès aux enfants sont contestés.",
            cost: "2000$ - 10000+"
          },
          {
            value: "financial",
            label: "Analyse financière / comptable",
             showIf: (ans) => ans.businessCount !== "0" || ans.hasInternational === "yes", // Show if business or international aspects
            description: "Pour évaluer des revenus complexes, des actifs cachés, ou des situations financières internationales.",
            cost: "1500$ - 8000+"
          },
          {
            value: "business",
            label: "Évaluation d'entreprise",
            showIf: (ans) => ans.businessCount !== "0",
            description: "Déterminer la valeur marchande d'une entreprise pour le partage.",
            cost: "5000$ - 15000+"
          },
           {
            value: "realEstate", // Added real estate expertise
            label: "Évaluation immobilière",
            showIf: (ans) => ans.propertyCount !== "0",
            description: "Déterminer la valeur marchande des propriétés pour le partage.",
            cost: "300$ - 600$ par propriété"
          },
          {
            value: "pension",
            label: "Évaluation actuarielle (régime de retraite)",
             showIf: (ans) => ans.hasPension === "yes", // Show if pensions exist
            description: "Calculer la valeur partageable des fonds de pension.",
            cost: "150$ - 2000+"
          }
        ],
        helpText: "Les expertises sont généralement requises en cas de désaccord ou pour évaluer des actifs complexes. Elles représentent une part importante des coûts dans les divorces contestés."
      }
    ]
  },
  {
    id: "otherServices",
    title: "Autres services",
    icon: "📋",
    description: "Aurez-vous besoin d'autres types de services ?",
    questions: [
      {
        name: "needHuissier",
        type: "radio",
        label: "Pensez-vous avoir besoin d'un huissier de justice ?",
        options: [
          { value: "yes", label: "Oui" },
          { value: "no", label: "Non / Ne sait pas" }
        ],
        helpText: "Nécessaire pour la signification officielle de documents si l'autre partie refuse de coopérer ou est difficile à joindre."
      },
      {
        name: "huissierServices",
        type: "slider",
        label: "Si oui, combien de significations environ ?",
        min: 1,
        max: 5, // Reduced max for simplicity, can be adjusted
        showIf: (ans) => ans.needHuissier === "yes",
        helpText: "Estimez le nombre de fois où un document devra être remis officiellement (demande, jugement, etc.). Coût: 80-200$ par signification en moyenne."
      }
    ]
  },
  {
    id: "summary", // Changed from step 6 to summary
    title: "Résumé et estimation",
    icon: "💰",
    description: "Voici une estimation basée sur vos réponses.",
    questions: [] // No questions in the summary section
  }
];

// Custom hook to store data in localStorage
function useLocalStorage(key, initialValue) {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.log("Error reading from localStorage:", error);
      return initialValue;
    }
  });

  const setValue = (value) => {
    try {
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.log("Error writing to localStorage:", error);
    }
  };

  return [storedValue, setValue];
}

// Replace CostBreakdownChart component with new percentage-based display
function CostBreakdownDisplay({ lineItems }) {
  return (
    <div className="w-full space-y-4">
      {lineItems.map((item, index) => (
        <div key={index} className="w-full">
          <div className="flex justify-between mb-1">
            <span className="text-sm font-medium">{item.label}</span>
            <span className="text-sm font-medium">{item.percentage}%</span>
          </div>
          <progress 
            className="progress progress-primary w-full" 
            value={item.percentage} 
            max="100"
          ></progress>
          <div className="text-xs text-gray-500 text-right mt-1">
            {item.min}$ - {item.max}$
          </div>
        </div>
      ))}
    </div>
  );
}

// Add user choices history component
function UserChoicesHistory({ answers, steps }) {
  const getChoiceLabel = (questionName, value, step) => {
    const question = step.questions.find(q => q.name === questionName);
    if (!question) return value;
    
    if (question.type === 'radio') {
      const option = question.options.find(opt => opt.value === value);
      return option ? option.label : value;
    }
    
    if (question.type === 'checkbox' && Array.isArray(value)) {
      return value.map(v => {
        const option = question.options.find(opt => opt.value === v);
        return option ? option.label : v;
      }).join(', ');
    }
    
    return value;
  };

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <h2 className="card-title">Vos choix</h2>
        <div className="divider"></div>
        {Object.entries(answers).map(([key, value]) => {
          const step = steps.find(s => s.questions.some(q => q.name === key));
          if (!step) return null;
          
          return (
            <div key={key} className="flex justify-between items-center py-2">
              <span className="text-sm font-medium">{
                step.questions.find(q => q.name === key)?.label
              }</span>
              <span className="badge badge-primary">{
                getChoiceLabel(key, value, step)
              }</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Composant principal
export default function DivorceCostEstimator() {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [activeSectionId, setActiveSectionId] = useState(sections[0].id);
  const [completedSections, setCompletedSections] = useLocalStorage("divorceCostCompletedSections", {});
  const [answers, setAnswers] = useLocalStorage("divorceCostAnswers", {});
  const [lineItems, setLineItems] = useState([]);
  const [totalCost, setTotalCost] = useState({ min: 0, max: 0 });
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showLegalAidInfo, setShowLegalAidInfo] = useState(true);
  const [language, setLanguage] = useState("fr"); // Pour une future implémentation multilingue
  const [showComparison, setShowComparison] = useState(false);
  const [faqOpenIndex, setFaqOpenIndex] = useState(null);
  const [expandedQuestions, setExpandedQuestions] = useState({});

  useEffect(() => {
    calculateCosts();
    // Automatically mark section as complete if all required questions are answered
    const currentSection = sections.find(s => s.id === activeSectionId);
    if (currentSection && isSectionComplete(currentSection.id)) {
      setCompletedSections(prev => ({ ...prev, [activeSectionId]: true }));
    }
    // eslint-disable-next-line
  }, [answers, activeSectionId]);

  // Toggle question expansion
  const toggleQuestionExpansion = (questionName) => {
    setExpandedQuestions(prev => ({ ...prev, [questionName]: !prev[questionName] }));
  };

  // Autosave des réponses lors des changements
  const handleChange = (name, value) => {
    setAnswers((prev) => {
      const newAnswers = { ...prev, [name]: value };
      // Auto-save as answers change (consider debouncing for performance if needed)
      try {
        localStorage.setItem("divorceCostAnswers", JSON.stringify(newAnswers));
      } catch (e) {
        console.log("Error saving to localStorage:", e);
      }
      return newAnswers;
    });
  };

  // Navigate to next section
  const handleNextSection = () => {
    const currentIndex = sections.findIndex(s => s.id === activeSectionId);
    if (currentIndex < sections.length - 1) {
       // Mark current section complete before moving
      if (isSectionComplete(activeSectionId)) {
        setCompletedSections(prev => ({ ...prev, [activeSectionId]: true }));
      }
      setActiveSectionId(sections[currentIndex + 1].id);
      window.scrollTo(0, 0); // Scroll to top on section change
    }
  };

  // Navigate to previous section
  const handlePrevSection = () => {
    const currentIndex = sections.findIndex(s => s.id === activeSectionId);
    if (currentIndex > 0) {
      setActiveSectionId(sections[currentIndex - 1].id);
      window.scrollTo(0, 0); // Scroll to top on section change
    }
  };

  // Go to a specific section
  const goToSection = (sectionId) => {
     const targetIndex = sections.findIndex(s => s.id === sectionId);
     const currentIndex = sections.findIndex(s => s.id === activeSectionId);

     // Allow navigation only to already visited or next available section
     let canNavigate = false;
     if (targetIndex <= currentIndex) {
       canNavigate = true; // Can always go back
     } else {
       // Check if all previous sections are complete
       let previousSectionsComplete = true;
       for (let i = 0; i < targetIndex; i++) {
         if (!completedSections[sections[i].id] && sections[i].questions.length > 0) {
            previousSectionsComplete = false;
            break;
          }
       }
       if (previousSectionsComplete) {
         canNavigate = true;
       }
     }

     if (canNavigate) {
       setActiveSectionId(sectionId);
       window.scrollTo(0, 0);
     } else {
       // Optionally show a message indicating sections must be completed first
       console.log("Please complete previous sections first.");
     }
  };

  // Amélioration de la fonction de réinitialisation avec confirmation
  const handleReset = () => {
    if (showResetConfirm) {
      setAnswers({});
      setCompletedSections({}); // Reset completed sections too
      setActiveSectionId(sections[0].id); // Go back to the first section
      setShowResetConfirm(false);
       window.scrollTo(0, 0);
    } else {
      setShowResetConfirm(true);
      // Auto-hide the confirmation after 5 seconds
      setTimeout(() => setShowResetConfirm(false), 5000);
    }
  };

  // Check if a section is complete
  const isSectionComplete = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section || section.questions.length === 0) return true; // Summary section is always complete

    return section.questions.every((q) => {
      // Skip questions that aren't shown based on conditions
      if (q.showIf && !q.showIf(answers)) return true;

      // Check if the question has a valid answer
      const answer = answers[q.name];
      if (q.type === 'checkbox') {
        // Checkbox is considered answered even if empty array (user interacted)
        return answer !== undefined;
      } else {
        return answer !== undefined && answer !== null && answer !== "";
      }
    });
  };

  // Calcul des coûts amélioré
  const calculateCosts = () => {
    let minSum = 0;
    let maxSum = 0;
    const newLineItems = [];

    // Get regional multiplier
    const region = REGIONS[answers.region || "OTHER"];
    const regionalMultiplier = region.lawyer_multiplier;

    // Calculate complexity multiplier
    let complexityMultiplier = 1.0;
    
    // Property complexity
    if (answers.propertyCount) {
      const propertyFactor = COMPLEXITY_FACTORS.PROPERTIES.find(
        f => f.count.toString() === answers.propertyCount
      );
      if (propertyFactor) complexityMultiplier *= propertyFactor.multiplier;
    }

    // Children complexity
    if (answers.childCount) {
      const childrenFactor = COMPLEXITY_FACTORS.CHILDREN.find(
        f => f.count.toString() === answers.childCount
      );
      if (childrenFactor) complexityMultiplier *= childrenFactor.multiplier;
    }

    // Business complexity
    if (answers.businessCount) {
      const businessFactor = COMPLEXITY_FACTORS.BUSINESSES.find(
        f => f.count.toString() === answers.businessCount
      );
      if (businessFactor) complexityMultiplier *= businessFactor.multiplier;
    }

    // International aspects
    if (answers.hasInternational === "yes") {
      complexityMultiplier *= COMPLEXITY_FACTORS.INTERNATIONAL;
    }

    // Court Fees
    if (answers.filingType) {
      let fee = 0;
      switch (answers.filingType) {
        case "conjointe":
          fee = OFFICIAL_FEES.DEMANDE_CONJOINTE;
          break;
        case "demande":
          fee = OFFICIAL_FEES.DEMANDE;
          break;
        case "reponse":
          fee = OFFICIAL_FEES.REPONSE;
          break;
        case "none":
          fee = 0;
          break;
      }
      
      // Add certification and copies if needed
      if (fee > 0) {
        fee += OFFICIAL_FEES.ADDITIONAL.CERTIFICATION;
        fee += OFFICIAL_FEES.ADDITIONAL.COPIES;
      }

      if (fee > 0) {
        newLineItems.push({
          label: "Frais de cour",
          min: fee,
          max: fee,
          icon: "⚖️",
          percentage: 0
        });
        minSum += fee;
        maxSum += fee;
      }
    }

    // Lawyer Fees
    if (answers.wantLawyer === "true") {
      let lawyerCostMin, lawyerCostMax;
      const rates = COST_RANGES.LAWYER.HOURLY_RATES[answers.lawyerExperience || "INTERMEDIATE"];
      
      // Base hourly rates
      lawyerCostMin = rates.min;
      lawyerCostMax = rates.max;

      // Apply regional multiplier
      lawyerCostMin *= regionalMultiplier;
      lawyerCostMax *= regionalMultiplier;

      // Apply complexity multiplier
      lawyerCostMin *= complexityMultiplier;
      lawyerCostMax *= complexityMultiplier;

      // Estimate hours based on case type
      let estimatedHours;
      if (answers.isContested === "yes") {
        estimatedHours = { min: 30, max: 60 }; // Contested cases need more hours
      } else {
        estimatedHours = { min: 10, max: 20 }; // Uncontested cases need fewer hours
      }

      // Calculate total lawyer costs
      const totalMin = lawyerCostMin * estimatedHours.min;
      const totalMax = lawyerCostMax * estimatedHours.max;

      // If shared lawyer in uncontested case, reduce costs
      if (answers.lawyerShared === "yes" && answers.isContested === "no") {
        lawyerCostMin = totalMin * 0.6; // 40% discount for shared representation
        lawyerCostMax = totalMax * 0.6;
      } else {
        lawyerCostMin = totalMin;
        lawyerCostMax = totalMax;
      }

      newLineItems.push({
        label: "Honoraires d'avocat",
        min: Math.round(lawyerCostMin),
        max: Math.round(lawyerCostMax),
        icon: "👨‍⚖️",
        percentage: 0
      });
      minSum += lawyerCostMin;
      maxSum += lawyerCostMax;
    }

    // Mediation Fees
    if (answers.wantMediation === "yes") {
      const freeHours = answers.childCount !== "0" 
        ? COST_RANGES.MEDIATION.FREE_HOURS.WITH_CHILDREN 
        : COST_RANGES.MEDIATION.FREE_HOURS.WITHOUT_CHILDREN;
      
      const estimatedHours = answers.isContested === "yes" ? 8 : 5;
      const paidHours = Math.max(0, estimatedHours - freeHours);
      
      const mediationCost = paidHours * COST_RANGES.MEDIATION.HOURLY_RATE;
      
      if (mediationCost > 0) {
        newLineItems.push({
          label: "Médiation familiale",
          min: mediationCost,
          max: mediationCost,
          icon: "🤝",
          percentage: 0
        });
        minSum += mediationCost;
        maxSum += mediationCost;
      }
    }

    // Huissier (Bailiff) Fees
    if (answers.needHuissier === "yes") {
      const baseRange = COST_RANGES.HUISSIER.BASE;
      let multiplier = 1.0;
      
      // Apply distance multiplier
      switch (answers.huissierDistance) {
        case "regional":
          multiplier = 1.5;
          break;
        case "far":
          multiplier = 2.0;
          break;
      }
      
      const services = Number(answers.huissierServices || 1);
      const minHuissier = baseRange.min * services * multiplier;
      const maxHuissier = baseRange.max * services * multiplier;
      
      newLineItems.push({
        label: `Huissier (${services} services)`,
        min: Math.round(minHuissier),
        max: Math.round(maxHuissier),
        icon: "📋",
        percentage: 0
      });
      minSum += minHuissier;
      maxSum += maxHuissier;
    }

    // Expert Fees
    if (answers.expertiseTypes) {
      answers.expertiseTypes.forEach((type) => {
        let expertiseCost;
        let label;
        
        switch (type) {
          case "psychosocial":
            expertiseCost = answers.childCount > "1" 
              ? COST_RANGES.EXPERTISE.PSYCHOSOCIAL.COMPLEX
              : COST_RANGES.EXPERTISE.PSYCHOSOCIAL.BASIC;
            label = expertiseCost.label;
            break;
          case "financial":
            expertiseCost = answers.businessCount !== "0"
              ? COST_RANGES.EXPERTISE.FINANCIAL.COMPLEX
              : COST_RANGES.EXPERTISE.FINANCIAL.SIMPLE;
            label = expertiseCost.label;
            break;
          case "business":
            expertiseCost = COST_RANGES.EXPERTISE.FINANCIAL.BUSINESS;
            label = expertiseCost.label;
            break;
          case "pension":
            expertiseCost = answers.hasInternational === "yes"
              ? COST_RANGES.EXPERTISE.PENSION.COMPLEX
              : COST_RANGES.EXPERTISE.PENSION.SIMPLE;
            label = expertiseCost.label;
            break;
        }

        if (expertiseCost) {
          // Apply complexity multiplier to expertise costs
          const minExpert = expertiseCost.min * complexityMultiplier;
          const maxExpert = expertiseCost.max * complexityMultiplier;
          
          newLineItems.push({
            label,
            min: Math.round(minExpert),
            max: Math.round(maxExpert),
            icon: "🔍",
            percentage: 0
          });
          minSum += minExpert;
          maxSum += maxExpert;
        }
      });
    }

    // Calculate percentages
    if (minSum > 0) {
      newLineItems.forEach((item) => {
        item.percentage = Math.round((item.min / minSum) * 100);
      });
    }

    setLineItems(newLineItems);
    setTotalCost({ 
      min: Math.round(minSum), 
      max: Math.round(maxSum),
      complexity: complexityMultiplier,
      region: region.name
    });
  };

  // Fonction de partage (pour les réseaux sociaux)
  const shareSummary = () => {
    // Dans une vraie implémentation, ceci permettrait un partage facile sur les réseaux sociaux
    alert(
      "Cette fonctionnalité n'est pas encore disponible. Partage d'estimation."
    );
  };

  // Composant de slider amélioré avec meilleure UI
  const renderSlider = (question) => {
    const value = parseInt(answers[question.name]) || question.min;

    return (
      <div className="w-full">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>{question.min}</span>
          <span>{question.max}</span>
        </div>
        <div className="flex items-center gap-4">
          <input
            type="range"
            min={question.min}
            max={question.max}
            value={value}
            onChange={(e) => handleChange(question.name, e.target.value)}
            className="range range-primary w-full"
          />
          <span className="bg-blue-100 text-blue-700 font-medium px-3 py-1 rounded-full w-16 text-center">
            {value}
          </span>
        </div>
      </div>
    );
  };

  const currentSection = sections.find(s => s.id === activeSectionId);
  const progressPercent = Math.round((sections.findIndex(s => s.id === activeSectionId) / (sections.length - 1)) * 100);

  // Astuces pour économiser améliorées
  const savingTips = [
    "Optez pour un divorce à l'amiable plutôt que contesté pour économiser sur les frais d'avocat.",
    "Envisagez la médiation familiale (souvent gratuite pour les premières séances).",
    "Préparez vos documents à l'avance pour réduire le temps passé avec votre avocat.",
    "Vérifiez votre admissibilité à l'aide juridique gratuite.",
    "Partagez un avocat dans le cas d'un divorce à l'amiable.",
    "Comparez les tarifs de plusieurs avocats avant de faire votre choix.",
    "Demandez un tarif forfaitaire plutôt qu'un tarif horaire.",
    "Utilisez les ressources en ligne pour comprendre le processus.",
    "Assistez à des ateliers gratuits sur le divorce offerts par des centres juridiques.",
    "Demandez si votre employeur offre une aide juridique dans son programme d'avantages sociaux.",
  ];

  // FAQ questions
  const faqItems = [
    {
      question: "Combien de temps dure un divorce au Québec ?",
      answer:
        "Un divorce à l'amiable prend généralement 3 à 6 mois, tandis qu'un divorce contesté peut durer de 1 à 3 ans selon la complexité du dossier.",
    },
    {
      question: "Puis-je obtenir une aide financière pour mon divorce ?",
      answer:
        "Oui, l'aide juridique peut couvrir vos frais de divorce si vous avez de faibles revenus. Vérifiez votre admissibilité auprès de la Commission des services juridiques.",
    },
    {
      question: "Quels sont les frais cachés d'un divorce ?",
      answer:
        "Certains frais souvent oubliés incluent les coûts de déménagement, la division des régimes de retraite, les frais d'évaluation de biens et les coûts liés à l'établissement d'un nouveau testament.",
    },
  ];

  // NEW: Function to render a single section
  const renderSection = (section) => {
    const isCurrent = section.id === activeSectionId;
    const isCompleted = completedSections[section.id] || false;
    const sectionIndex = sections.findIndex(s => s.id === section.id);
    const isSummary = section.id === 'summary';

    // Determine if the section is accessible
    let isAccessible = false;
     if (sectionIndex === 0) {
        isAccessible = true; // First section always accessible
      } else {
        // Check if all previous sections are complete
        let previousSectionsComplete = true;
        for (let i = 0; i < sectionIndex; i++) {
          // Allow skipping sections with no questions (like potentially future info-only sections)
           if (!completedSections[sections[i].id] && sections[i].questions.length > 0) {
             previousSectionsComplete = false;
             break;
           }
        }
        isAccessible = previousSectionsComplete;
     }

    return (
      <div key={section.id} className={`mb-6 rounded-xl border shadow-sm transition-all duration-300 ${
        isCurrent ? 'border-blue-300 bg-blue-50 shadow-md' : isCompleted ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-white'
      } ${isAccessible ? 'opacity-100' : 'opacity-60 cursor-not-allowed'}`}>
        <button
          className={`w-full p-5 text-left flex justify-between items-center ${isAccessible ? 'cursor-pointer' : 'cursor-not-allowed'}`}
          onClick={() => isAccessible && goToSection(section.id)}
          disabled={!isAccessible}
          aria-expanded={isCurrent}
        >
          <div className="flex items-center">
            <span className={`mr-3 text-xl ${isCompleted && !isCurrent ? 'text-green-600' : isCurrent ? 'text-blue-600' : 'text-slate-500'}`}>
              {section.icon}
            </span>
            <h2 className={`text-lg font-semibold ${isCompleted && !isCurrent ? 'text-green-800' : isCurrent ? 'text-blue-800' : 'text-slate-700'}`}>
              {section.title}
            </h2>
          </div>
          <div className="flex items-center">
            {isCompleted && !isCurrent && (
              <CheckCircleIcon className="w-6 h-6 text-green-500 mr-2" />
            )}
             <span className={`transform transition-transform duration-200 ${isCurrent ? 'rotate-180' : 'rotate-0'}`}>
               {isAccessible ? <ChevronDownIcon className="w-5 h-5 text-slate-500" /> : <LockClosedIcon className="w-5 h-5 text-slate-400" /> }
             </span>
          </div>
        </button>

        {/* Collapsible Content - Corrected Structure */}
        {isCurrent && ( 
          <div className="px-5 pb-6 pt-2 border-t border-slate-200">
            {isSummary ? (
              // --- Render Summary Content --- 
              renderSummaryContent()
            ) : (
              // --- Render Question Section Content --- 
              <>
                <p className="text-sm text-slate-600 mb-6">{section.description}</p>
                <div className="space-y-6">
                  {section.questions.map((question, qIndex) => {
                    if (question.showIf && !question.showIf(answers)) return null;

                    return (
                      <div key={qIndex} className="p-4 rounded-lg border border-slate-200 bg-white shadow-sm">
                         <div className="flex items-start justify-between gap-2">
                           <label className="font-medium text-slate-800 flex-1">
                             {question.label}
                           </label>
                           {/* Simple Info Icon for help text */}
                           {question.helpText && (
                             <Tooltip id={`tooltip-${question.name}`}>
                               <button
                                 type="button"
                                 className="text-blue-500 hover:text-blue-700"
                                 data-tooltip-id={`tooltip-${question.name}`}
                                 data-tooltip-content={question.helpText}
                               >
                                 <InformationCircleIcon className="w-5 h-5" />
                               </button>
                             </Tooltip>
                           )}
                         </div>
                          {/* Optional: Keep detailed help text collapsible */}
                         {/* {question.helpText && (
                           <p className={`text-sm text-slate-500 mt-1 ${expandedQuestions[question.name] ? 'block' : 'hidden'}`}>
                             {question.helpText}
                           </p>
                         )} */}

                         {/* Question inputs based on type */}
                         <div className="mt-3 space-y-2">
                            {question.type === "radio" && (
                              <div className="space-y-2">
                                {question.options.map((opt, i) => {
                                  if (opt.showIf && !opt.showIf(answers)) return null;
                                  const isChecked = answers[question.name] === opt.value;
                                  return (
                                    <label
                                      key={i}
                                      className={`cursor-pointer flex items-start gap-3 p-3 rounded-md border transition-colors ${
                                        isChecked ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'
                                      }`}
                                    >
                                      <input
                                        type="radio"
                                        name={question.name}
                                        value={opt.value}
                                        checked={isChecked}
                                        onChange={(e) => handleChange(question.name, e.target.value)}
                                        className="radio radio-primary mt-1 flex-shrink-0"
                                      />
                                      <span className="flex-1">
                                        <span className="text-slate-700 block">{opt.label}</span>
                                        {opt.helpText && <span className="text-xs text-slate-500 block mt-1">{opt.helpText}</span>}
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}

                            {question.type === "slider" && (
                              <div className="w-full">
                                <div className="flex items-center gap-4">
                                  <input
                                    type="range"
                                    min={question.min}
                                    max={question.max}
                                    value={answers[question.name] || question.min}
                                    onChange={(e) => handleChange(question.name, e.target.value)}
                                    className="range range-primary w-full"
                                  />
                                  <span className="bg-blue-100 text-blue-700 font-medium px-3 py-1 rounded-full w-16 text-center">
                                    {answers[question.name] || question.min}
                                  </span>
                                </div>
                                 <div className="flex justify-between text-xs text-slate-500 mt-1 px-1">
                                  <span>{question.min}</span>
                                  <span>{question.max}</span>
                                </div>
                              </div>
                            )}

                             {question.type === "checkbox" && (
                                <div className="space-y-3">
                                  {question.options.map((opt, i) => {
                                     if (opt.showIf && !opt.showIf(answers)) return null;
                                     const isChecked = answers[question.name]?.includes(opt.value) || false;
                                     return (
                                        <div key={i} className={`rounded-lg border p-4 transition-colors ${isChecked ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                                          <label className="flex items-start gap-3 cursor-pointer">
                                            <input
                                              type="checkbox"
                                              value={opt.value}
                                              checked={isChecked}
                                              onChange={(e) => {
                                                const currentValues = answers[question.name] || [];
                                                const newValues = e.target.checked
                                                  ? [...currentValues, opt.value]
                                                  : currentValues.filter(v => v !== opt.value);
                                                handleChange(question.name, newValues);
                                              }}
                                              className="mt-1 checkbox checkbox-primary flex-shrink-0"
                                            />
                                            <div className="flex-1">
                                              <div className="font-medium text-slate-700">{opt.label}</div>
                                              {opt.description && (
                                                <p className="text-sm text-slate-500 mt-1">{opt.description}</p>
                                              )}
                                              {opt.cost && (
                                                <div className="text-sm text-blue-600 font-medium mt-1">
                                                  Estimation: {opt.cost}
                                                </div>
                                              )}
                                            </div>
                                          </label>
                                        </div>
                                     )
                                  })}
                                </div>
                             )}
                         </div>
                      </div>
                    );
                  })}

                  {/* Navigation Buttons */}
                  <div className="mt-8 flex justify-between items-center pt-4 border-t border-slate-200">
                    <div> 
                      {sectionIndex > 0 && (
                        <button
                          onClick={handlePrevSection}
                          className="btn btn-outline btn-primary btn-sm gap-1"
                        >
                          <ArrowLeftCircleIcon className="w-4 h-4" />
                          Précédent
                        </button>
                      )}
                    </div>
                    <div>
                      {sectionIndex < sections.length - 1 && (
                        <button
                          onClick={handleNextSection}
                          className="btn btn-primary btn-sm gap-1"
                          disabled={!isSectionComplete(section.id)} // Disable if current section not complete
                        >
                          { isSectionComplete(section.id) ? 'Suivant' : 'Complétez la section'}
                          <ArrowRightCircleIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // New component for the summary section content
  const renderSummaryContent = () => {
    if (activeSectionId !== 'summary') return null;

    return (
      <div className="space-y-8 px-4 mb-8">
        {/* Top Summary: Total Cost & Complexity */}
        <div className="grid md:grid-cols-2 gap-6">
           <div className="card bg-gradient-to-br from-blue-500 to-blue-700 text-white p-6 rounded-xl shadow-lg">
              <h3 className="font-semibold mb-3 text-lg flex items-center opacity-90">
                 <span className="mr-2">💰</span> Estimation Totale
              </h3>
              <p className="text-4xl font-bold">
                {totalCost.min.toFixed(0)}$ - {totalCost.max.toFixed(0)}$
              </p>
              <p className="opacity-80 mt-1">Coût total estimé de la procédure</p>
           </div>
           <div className="card bg-white border border-slate-200 p-6 rounded-xl shadow">
             <h3 className="font-semibold text-slate-700 mb-3 flex items-center">
               <span className="mr-2">📊</span> Facteurs Clés
             </h3>
              <ul className="space-y-2 text-sm">
                <li className="flex justify-between"><span>Région:</span> <span className="font-medium">{totalCost.region || 'N/A'}</span></li>
                <li className="flex justify-between"><span>Type:</span> <span className="font-medium">{answers.isContested === 'yes' ? 'Contesté' : 'À l\'amiable'}</span></li>
                <li className="flex justify-between"><span>Complexité (Multiplicateur):</span> <span className="font-medium">{totalCost.complexity?.toFixed(2) || '1.00'}x</span></li>
                {/* Add more factors like lawyer choice, mediation etc. */}
               </ul>
           </div>
        </div>

        {/* Detailed Cost Breakdown Card - Updated */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-xl font-bold text-blue-700 mb-4 flex items-center gap-2">
            <span className="bg-blue-50 p-2 rounded-full">📊</span>
            Détail des coûts
          </h2>
          {lineItems.length > 0 ? (
            <CostBreakdownDisplay lineItems={lineItems} />
          ) : (
            <div className="flex flex-col items-center justify-center h-32 bg-slate-50 rounded-lg">
              <ExclamationCircleIcon className="w-12 h-12 text-slate-300 mb-2" />
              <p className="text-slate-500 italic">Aucune donnée disponible</p>
            </div>
          )}
        </div>

        {/* Contextual Saving Tips Card */}
        <div className="bg-green-50 rounded-xl border border-green-200 shadow-sm p-6">
          <h2 className="text-xl font-bold text-green-700 mb-4 flex items-center gap-2">
            <span className="bg-green-100 p-2 rounded-full">💡</span>
            Astuces personnalisées pour économiser
          </h2>
          <div className="space-y-4">
            {SAVING_TIPS
              // Filter tips based on answers (example logic, can be refined)
              .filter(tip => {
                if (tip.title.includes("médiation") && answers.wantMediation === 'no') return false; // Don't show mediation tip if user said no
                if (tip.title.includes("conjoint") && answers.isContested === 'yes') return false; // Don't show joint filing tip if contested
                if (tip.title.includes("documents") && answers.wantLawyer !== 'true') return false; // Show document prep tip only if hiring lawyer
                return true;
              })
              .slice(0, 3) // Show top 3 relevant tips
              .map((tip, index) => (
              <div key={index} className="flex items-start gap-4 bg-white p-4 rounded-lg border border-green-100 shadow-sm">
                <div className="bg-green-100 p-2 rounded-full flex-shrink-0 mt-1">
                  <CheckCircleIcon className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-green-800">{tip.title}</h4>
                  <p className="text-green-700 text-sm mt-1">{tip.description}</p>
                  {tip.potential_savings && <p className="text-xs text-green-600 font-medium mt-1">Économie potentielle: {tip.potential_savings}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>

         {/* Next Steps Card */}
        <div className="bg-blue-50 rounded-xl border border-blue-200 shadow-sm p-6">
           <h2 className="text-xl font-bold text-blue-700 mb-4 flex items-center gap-2">
             <span className="bg-blue-100 p-2 rounded-full">🚀</span>
             Prochaines étapes suggérées
           </h2>
           <div className="space-y-3 text-sm">
              <p className="text-blue-800">Cette estimation est un point de départ. Voici quelques actions que vous pourriez envisager :</p>
              <ul className="list-disc list-inside space-y-2 pl-2 text-blue-700">
                {answers.wantMediation !== 'no' && <li><a href="#" className="font-medium hover:underline">En savoir plus sur la médiation familiale</a> (souvent la méthode la plus économique).</li>}
                <li><a href="/orientation/aide-juridique" className="font-medium hover:underline">Vérifier votre admissibilité à l'aide juridique</a> (peut couvrir tous vos frais).</li>
                <li><a href="/guides/divorce" className="font-medium hover:underline">Consulter notre guide complet sur le divorce</a> pour comprendre la procédure.</li>
                {answers.wantLawyer === 'true' && <li><a href="/orientation/cabinets" className="font-medium hover:underline">Chercher un avocat spécialisé en droit familial</a> dans votre région.</li>}
                <li>Préparer vos documents financiers importants (avis de cotisation, relevés bancaires, etc.).</li>
              </ul>
               <p className="text-xs text-blue-600 pt-3 border-t border-blue-100 mt-4">N'oubliez pas que chaque situation est unique. Consultez un professionnel pour des conseils adaptés.</p>
           </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto pb-12">
      {/* Modern Header */}
      <div className="px-4 md:px-6 py-8 mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100 shadow-sm mb-8">
        <h1 className="text-2xl font-bold text-blue-700 mb-3 text-center">
          <span className="inline-block h-1 bg-warning w-8 mr-2 rounded-full align-middle"></span>
          Calculateur des coûts du divorce
          <span className="inline-block h-1 bg-warning w-8 ml-2 rounded-full align-middle"></span>
        </h1>
        <p className="text-center text-blue-600 max-w-3xl mx-auto">
          Estimez les coûts potentiels de votre procédure de divorce selon votre situation personnelle.
        </p>
      </div>

      {/* Top cards: cost summary and progression */}
      <div className="grid md:grid-cols-2 gap-6 mb-8 px-4">
        {/* Card 1: Cost Summary */}
        <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
          <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
            <span className="bg-blue-50 p-2 rounded-full mr-2">💰</span>
            Estimation des coûts
          </h3>
          <div className="flex items-center justify-center h-16">
            {totalCost.min === 0 && totalCost.max === 0 ? (
              <p className="text-slate-500 text-center text-sm">
                Complétez le questionnaire pour voir l'estimation
              </p>
            ) : (
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-700">
                  {totalCost.min.toFixed(0)}$ - {totalCost.max.toFixed(0)}$
                </p>
                <p className="text-sm text-slate-600">coût total estimé</p>
              </div>
            )}
          </div>
        </div>

        {/* Card 2: Progression */}
        <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
          <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
            <span className="bg-blue-50 p-2 rounded-full mr-2">📊</span>
            Progression du questionnaire
          </h3>
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="font-semibold">{progressPercent}% complété</span>
              <span className="text-slate-500 text-xs">
                {sections.findIndex(s => s.id === activeSectionId) + 1} / {sections.length} étapes
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-3">
              <div
                className="bg-blue-500 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Legal Aid Info - Moved here and restyled */}
      {showLegalAidInfo && (
        <div className="mb-8 px-4">
          <div className="bg-info-50 rounded-xl border border-info-200 p-6 relative">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-3 mb-3">
                <span className="bg-blue-50 p-2 rounded-full">ℹ️</span>
                <h3 className="text-lg font-semibold text-blue-800">
                  Aide juridique disponible
                </h3>
              </div>
              <button
                onClick={() => setShowLegalAidInfo(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Fermer"
              >
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-3 text-blue-900">
              <p className="font-medium">
                Vous avez de faibles revenus ou êtes bénéficiaires de l'aide sociale ?
              </p>
              <p className="text-sm">
                Vous pourriez être admissibles à l'aide juridique et ne pas avoir à payer
                ces frais. Un avocat pourrait par ailleurs effectuer vos démarches de
                divorce pour vous, sans frais.
              </p>
              <div className="flex items-center gap-2 mt-2">
                <a
                  href="https://www.csj.qc.ca/commission-des-services-juridiques/aide-juridique/admissibilite/fr"
                  className="text-blue-600 hover:text-blue-800 underline text-sm font-medium flex items-center gap-1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Suis-je admissible financièrement ?
                  <ArrowRightCircleIcon className="w-4 h-4" />
                </a>
              </div>
              <p className="text-sm mt-4 pt-4 border-t border-blue-100">
                <strong>Note importante :</strong> Si l'un de vous est bénéficiaire d'une aide financière de
                dernier recours, vous devez informer l'agent du ministère du Travail, de
                l'Emploi et de la Solidarité sociale (MTESS) en charge de votre dossier
                de vos démarches de divorce.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main questionnaire section - RENDER SECTIONS */}
      <div className="px-4">
        {sections.map(renderSection)}
      </div>

      {/* Cost breakdown section - Rendered inside summary section now */}
      {activeSectionId === 'summary' && renderSummaryContent()}

      {/* Reset button */}
      <div className="px-4 mt-8 flex justify-center">
        <button
          onClick={handleReset}
          className={`btn ${showResetConfirm ? 'btn-error' : 'btn-outline'} btn-sm hover:bg-red-600 transition-colors gap-2`}
        >
          <ArrowPathIcon className="w-4 h-4" />
          {showResetConfirm ? 'Confirmer la réinitialisation' : 'Réinitialiser'}
        </button>
      </div>
    </div>
  );
}
